import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import type { TierDTO } from '@uturn/api/v1';
import {
	Button,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Icon,
	sonner,
} from '@uturn/ui-kit';
import { Trans, useTranslation } from 'react-i18next';

export const PriceCalculatorSubscriptionConfirmDialog = ({
	tier,
	isSubscribing,
	onSubscribe,
	onClose,
}: {
	tier: TierDTO;
	isSubscribing: boolean;
	onSubscribe: (tierId: number) => void;
	onClose: () => void;
}) => {
	const { t } = useTranslation();

	return (
		<>
			<DialogHeader>
				<DialogTitle>
					{t(
						'pages.subscriptions.modal.confirm.title',
						'Confirmation of subscription',
					)}
				</DialogTitle>
				<div className="flex flex-col gap-3 leading-6">
					<p>
						{t(
							'pages.subscriptions.modal.confirm.description',
							'You are about to start a "{{tierName}}" subscription for the price calculator.',
							{ tierName: tier.name },
						)}
					</p>
					<p>
						{t(
							'pages.subscriptions.modal.confirm.details-heading',
							'Here are some more details:',
						)}
					</p>
					<ul className="list-disc pl-5">
						<li>
							{t(
								'pages.subscriptions.modal.confirm.detail-ongoing',
								'On-going subscription',
							)}
						</li>
						<li>
							{t(
								'pages.subscriptions.modal.confirm.detail-yearly',
								'Yearly invoiced & terminable',
							)}
						</li>
						<li>
							{t(
								'pages.subscriptions.modal.confirm.detail-advance',
								'Invoiced in advance',
							)}
						</li>
						<li>
							{t(
								'pages.subscriptions.modal.confirm.detail-payment-term',
								'Payment term of 10 days',
							)}
						</li>
					</ul>
					<p>
						{t(
							'pages.subscriptions.modal.confirm.usage-note',
							'You can already use the price calculator after you have started the subscription.',
						)}
					</p>
					<p>
						<Trans
							i18nKey="pages.subscriptions.modal.confirm.agreement"
							defaults='Selecting "Agree and subscribe" indicates your agreement with the <0>terms and conditions</0>.'
							components={[
								// biome-ignore lint/a11y/useAnchorContent: Trans injects children from translation string
								<a
									key="terms-link"
									rel="noreferrer"
									href="https://portal.uturn-system.com/assets/documents/PCS contract.pdf"
									target="_blank"
									className="font-bold text-foreground underline hover:text-foreground/75"
								/>,
							]}
						/>
					</p>
					<p>
						{t(
							'pages.subscriptions.modal.confirm.confirmation-question',
							'Are you sure you want to start this subscription?',
						)}
					</p>
				</div>
			</DialogHeader>

			<DialogFooter>
				<Button disabled={isSubscribing} onClick={onClose} variant="secondary">
					{t('general.cancel', 'Cancel')}
				</Button>
				<Button
					disabled={isSubscribing}
					onClick={() =>
						!tier.id
							? sonner.error('Tier identifier not found ')
							: onSubscribe(tier.id)
					}
				>
					{isSubscribing ? (
						<>
							<Icon className="mr-2 size-4" icon={faSpinnerThird} spin />
							{t('general.processing', 'Processing...')}
						</>
					) : (
						t('pages.subscriptions.modal.confirm.submit', 'Agree and subscribe')
					)}
				</Button>
			</DialogFooter>
		</>
	);
};
